import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { brandInfo, getAllLocations } from '../data/locations';
import { schoolInfo } from '../data/mock';
import { Instagram, MapPin, ChevronUp } from 'lucide-react';

const Footer = ({ location = null }) => {
  const { t } = useLanguage();
  const routerLocation = useLocation();
  const locations = getAllLocations();
  
  const isMainPage = routerLocation.pathname === '/' || routerLocation.pathname === '/gallery';

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Use Surabaya info for main page, location info for subpages
  const displayInfo = isMainPage ? schoolInfo : (location?.schoolInfo || brandInfo);
  const instagram = displayInfo.instagram || brandInfo.instagram;
  const logoUrl = isMainPage ? schoolInfo.logoUrl : brandInfo.logoUrl;
  const displayName = isMainPage ? schoolInfo.name : brandInfo.name;

  const navLinks = [
    { labelEn: 'About', labelId: 'Tentang', href: '#about' },
    { labelEn: 'Classes', labelId: 'Kelas', href: '#classes' },
    { labelEn: 'Instructor', labelId: 'Instruktur', href: '#instructor' },
    { labelEn: 'Gallery', labelId: 'Galeri', href: '#gallery' },
    { labelEn: 'Contact', labelId: 'Kontak', href: '#contact' },
  ];

  // Filter out gallery if location doesn't have one
  const filteredNavLinks = (!isMainPage && location && !location.hasGallery) 
    ? navLinks.filter(link => link.href !== '#gallery')
    : navLinks;

  return (
    <footer className="bg-[#1e1919] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img
                src={logoUrl}
                alt={displayName}
                className="w-14 h-14 rounded-full object-cover"
              />
              <span className="font-bold text-xl">{displayName}</span>
            </div>
            <p className="text-white/70 mb-6 leading-relaxed text-sm">
              {isMainPage 
                ? t(
                    'Professional ballet training for children aged 2-10 years in Surabaya, Indonesia.',
                    'Pelatihan balet profesional untuk anak usia 2-10 tahun di Surabaya, Indonesia.'
                  )
                : t(
                    'International ballet academy nurturing young dancers across Asia and Africa.',
                    'Akademi balet internasional yang membina penari muda di seluruh Asia dan Afrika.'
                  )
              }
            </p>
            <div className="flex items-center gap-2 text-white/70">
              <MapPin className="w-4 h-4" />
              <span>{isMainPage ? schoolInfo.location : (location?.schoolInfo?.location || 'International')}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-6">{t('Quick Links', 'Tautan Cepat')}</h3>
            <ul className="space-y-3">
              {filteredNavLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {t(link.labelEn, link.labelId)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h3 className="font-bold text-lg mb-6">{t('Our Studios', 'Studio Kami')}</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className={`text-sm hover:text-white transition-colors flex items-center gap-2 ${
                    isMainPage ? 'text-white' : 'text-white/70'
                  }`}
                >
                  <MapPin className="w-3 h-3" />
                  Surabaya, Indonesia
                </Link>
              </li>
              {locations.filter(loc => loc.slug !== 'surabaya').map((loc) => (
                <li key={loc.id}>
                  <Link
                    to={`/standorte/${loc.slug}`}
                    className={`text-sm hover:text-white transition-colors flex items-center gap-2 ${
                      location?.id === loc.id ? 'text-white' : 'text-white/70'
                    }`}
                  >
                    <MapPin className="w-3 h-3" />
                    {loc.city}, {loc.country}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social & Contact */}
          <div>
            <h3 className="font-bold text-lg mb-6">{t('Follow Us', 'Ikuti Kami')}</h3>
            <a
              href={instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 hover:bg-white/20 px-5 py-3 rounded-xl transition-colors mb-4"
            >
              <Instagram className="w-5 h-5" />
              <span className="text-sm">@studioballetizora</span>
            </a>
            <p className="text-white/70 text-sm">
              {t(
                'Follow us for updates, photos, and more!',
                'Ikuti kami untuk update, foto, dan lainnya!'
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm">
              © {new Date().getFullYear()} {displayName}. 
              {t(' All rights reserved.', ' Hak cipta dilindungi.')}
            </p>
            <button
              onClick={scrollToTop}
              className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm"
            >
              {t('Back to Top', 'Kembali ke Atas')}
              <ChevronUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
